import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Repeat } from 'lucide-react';

const keyframeCSS = `
  @media (max-width: 600px) {
    .offer-card {
      padding: 16px 20px !important;
      max-width: 100% !important;
    }
    .offer-amount {
      font-size: clamp(1.8rem, 8vw, 2.5rem) !important;
    }
    .offer-label {
      font-size: 0.72rem !important;
    }
  }
  @keyframes offerPhoneRing {
    0%, 100% { transform: rotate(0deg); }
    8%, 24% { transform: rotate(-18deg); }
    16%, 32% { transform: rotate(18deg); }
    40% { transform: rotate(0deg); }
  }
  @keyframes offerBorderGlow {
    0%, 100% { border-color: rgba(212,175,55,0.3); box-shadow: 0 0 20px rgba(212,175,55,0.1), inset 0 0 20px rgba(212,175,55,0.05); }
    50% { border-color: rgba(212,175,55,0.6); box-shadow: 0 0 40px rgba(212,175,55,0.2), inset 0 0 30px rgba(212,175,55,0.08); }
  }
  @keyframes offerNeonText {
    0%, 19%, 21%, 23%, 25%, 54%, 56%, 100% {
      text-shadow: 0 0 10px rgba(212,175,55,0.6), 0 0 20px rgba(212,175,55,0.3), 0 0 40px rgba(212,175,55,0.15);
      opacity: 1;
    }
    20%, 24%, 55% {
      text-shadow: 0 0 5px rgba(212,175,55,0.2);
      opacity: 0.8;
    }
  }
  @keyframes slotSpin {
    0% { transform: translateY(-20px); opacity: 0; }
    60% { transform: translateY(3px); opacity: 1; }
    100% { transform: translateY(0); opacity: 1; }
  }
  @keyframes offerShine {
    0% { left: -100%; }
    50%, 100% { left: 200%; }
  }
`;

function formatEuro(amount) {
  if (typeof amount !== 'number') return String(amount);
  return amount.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: amount < 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

const statusColors = {
  pending: { backgroundColor: 'rgba(243, 156, 18, 0.15)', color: '#f39c12', borderColor: 'rgba(243,156,18,0.3)' },
  accepted: { backgroundColor: 'rgba(46, 204, 113, 0.15)', color: '#2ecc71', borderColor: 'rgba(46,204,113,0.3)' },
  rejected: { backgroundColor: 'rgba(231, 76, 60, 0.15)', color: '#e74c3c', borderColor: 'rgba(231,76,60,0.3)' },
};

const statusLabels = {
  pending: 'In attesa',
  accepted: 'Accettata',
  rejected: 'Rifiutata',
};

export default function OfferDisplay({ lastOffer }) {
  const [displayAmount, setDisplayAmount] = useState(null);

  useEffect(() => {
    if (!lastOffer || lastOffer.amount === undefined || lastOffer.amount === null) {
      setDisplayAmount(null);
      return;
    }

    // Slot machine counter effect
    const target = lastOffer.amount;
    const steps = 12;
    const stepDuration = 50;
    let step = 0;

    const interval = setInterval(() => {
      step++;
      if (step >= steps) {
        setDisplayAmount(target);
        clearInterval(interval);
      } else {
        // Show random amounts getting closer to target
        const randomFactor = 1 - (step / steps);
        const randomOffset = (Math.random() - 0.5) * target * randomFactor * 2;
        setDisplayAmount(Math.max(0, Math.round(target + randomOffset)));
      }
    }, stepDuration);

    return () => clearInterval(interval);
  }, [lastOffer]);

  if (!lastOffer) return null;

  const { amount, status, swap_box_number, swap_region_name } = lastOffer;
  const isSwap = !!swap_box_number;

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '28px 32px',
    background: 'linear-gradient(135deg, rgba(15,15,40,0.95), rgba(20,20,50,0.9))',
    borderRadius: '16px',
    border: '2px solid rgba(212,175,55,0.3)',
    width: '100%',
    maxWidth: '500px',
    position: 'relative',
    overflow: 'hidden',
    animation: 'offerBorderGlow 2.5s ease-in-out infinite',
  };

  const shineOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '50%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.05), transparent)',
    animation: 'offerShine 4s ease-in-out infinite',
    pointerEvents: 'none',
  };

  const phoneContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };

  const phoneIconStyle = {
    color: '#D4AF37',
    animation: 'offerPhoneRing 2s ease-in-out infinite',
    filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.5))',
  };

  const labelStyle = {
    fontFamily: 'var(--font-display)',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#D4AF37',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    animation: 'offerNeonText 3s ease-in-out infinite',
  };

  const amountStyle = {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #D4AF37, #F0D060, #D4AF37)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    letterSpacing: '0.02em',
    textShadow: 'none',
    filter: 'drop-shadow(0 0 20px rgba(212,175,55,0.4))',
    animation: displayAmount === amount ? 'none' : 'slotSpin 0.1s ease-out',
  };

  const statusStyle = {
    fontFamily: 'var(--font-display)',
    fontSize: '0.8rem',
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    padding: '5px 18px',
    borderRadius: '9999px',
    border: '1px solid',
    ...(statusColors[status] || statusColors.pending),
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="offer-card"
        key={`${amount}-${status}`}
        style={containerStyle}
        initial={{ opacity: 0, scale: 0.85, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.85, y: -15 }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
      >
        <style>{keyframeCSS}</style>

        {/* Shine overlay */}
        <div style={shineOverlayStyle} />

        {/* Phone icon + label */}
        <div style={phoneContainerStyle}>
          <div style={phoneIconStyle}>
            {isSwap ? <Repeat size={22} /> : <Phone size={22} />}
          </div>
          <span className="offer-label" style={labelStyle}>
            {isSwap ? 'IL DOTTORE PROPONE UNO SCAMBIO' : 'IL DOTTORE PROPONE'}
          </span>
        </div>

        {/* Amount or swap info */}
        {isSwap ? (
          <motion.div
            style={{ ...amountStyle, fontSize: 'clamp(1.8rem, 4vw, 2.5rem)' }}
            initial={{ scale: 0, rotate: -5 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 180, damping: 14, delay: 0.2 }}
          >
            Pacco {swap_box_number}
            {swap_region_name && (
              <span style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: 400,
                WebkitTextFillColor: 'rgba(212,175,55,0.7)',
                marginTop: '4px',
              }}>
                {swap_region_name}
              </span>
            )}
          </motion.div>
        ) : (
          <motion.div
            style={amountStyle}
            initial={{ scale: 0, rotate: -5 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 180, damping: 14, delay: 0.2 }}
          >
            {formatEuro(displayAmount !== null ? displayAmount : amount)}
          </motion.div>
        )}

        {/* Status badge */}
        {status && (
          <motion.div
            style={statusStyle}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {statusLabels[status] || status}
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
